import {
  createSlice,
  createAsyncThunk,
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit';

import sub from 'date-fns/sub';
import axios from 'axios';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const FEATURE_URL =
  `${API_BASE_URL}/api/v1/posts`;

const FeatureName = 'posts';

export const FeatureForCamelCase = 'Posts';
export const FeatureForCamelCaseCRUD = 'Post';

const emptyReactions = () => ({
  thumbsUp: 0,
  wow: 0,
  heart: 0,
  rocket: 0,
  coffee: 0,
});

const normalizePost = (
  post,
  fallbackDate = new Date().toISOString()
) => ({
  ...post,

  date:
    post?.date ||
    post?.created_at ||
    fallbackDate,

  reactions: {
    ...emptyReactions(),
    ...(post?.reactions || {}),
  },
});

const postsAdapter = createEntityAdapter({
  selectId: (entity) => entity.id,

  sortComparer: (a, b) =>
    (b.date || '').localeCompare(
      a.date || ''
    ),
});

export const fetchers = {
  [`fetch${FeatureForCamelCase}`]:
    createAsyncThunk(
      `${FeatureName}/fetch${FeatureForCamelCase}`,
      async (_, { rejectWithValue }) => {
        try {
          const response =
            await axios.get(FEATURE_URL);

          if (!Array.isArray(response.data)) {
            return rejectWithValue(
              'Posts API returned an invalid response.'
            );
          }

          let minuteOffset = 1;

          return response.data.map((post) => {
            const fallbackDate = sub(
              new Date(),
              {
                minutes: minuteOffset++,
              }
            ).toISOString();

            return normalizePost(
              post,
              fallbackDate
            );
          });
        } catch (error) {
          return rejectWithValue(
            error.response?.data?.error ||
            error.message ||
            'Unable to load posts.'
          );
        }
      }
    ),
};

export const adders = {
  [`addNew${FeatureForCamelCaseCRUD}`]:
    createAsyncThunk(
      `${FeatureName}/addNew${FeatureForCamelCaseCRUD}`,
      async (
        newData,
        { rejectWithValue }
      ) => {
        try {
          const response =
            await axios.post(
              FEATURE_URL,
              newData
            );

          let data = response.data;

          if (typeof data === 'string') {
            try {
              data = JSON.parse(data);
            } catch (parseError) {
              return rejectWithValue(
                'Posts API returned an invalid response.'
              );
            }
          }

          if (
            !data ||
            typeof data !== 'object'
          ) {
            return rejectWithValue(
              'Posts API returned an invalid response.'
            );
          }

          return normalizePost(data);
        } catch (error) {
          return rejectWithValue(
            error.response?.data?.error ||
            error.message ||
            'Unable to create post.'
          );
        }
      }
    ),
};

export const updaters = {
  [`update${FeatureForCamelCaseCRUD}`]:
    createAsyncThunk(
      `${FeatureName}/update${FeatureForCamelCaseCRUD}`,
      async (
        updatedData,
        { rejectWithValue }
      ) => {
        try {
          const { id } = updatedData;

          const response =
            await axios.put(
              `${FEATURE_URL}/${id}`,
              updatedData
            );

          if (
            !response.data ||
            typeof response.data !== 'object'
          ) {
            return rejectWithValue(
              'Posts API returned an invalid response.'
            );
          }

          return normalizePost(
            response.data
          );
        } catch (error) {
          return rejectWithValue(
            error.response?.data?.error ||
            error.message ||
            'Unable to update post.'
          );
        }
      }
    ),
};

export const deleters = {
  [`delete${FeatureForCamelCaseCRUD}`]:
    createAsyncThunk(
      `${FeatureName}/delete${FeatureForCamelCaseCRUD}`,
      async (
        id,
        { rejectWithValue }
      ) => {
        try {
          const response =
            await axios.delete(
              `${FEATURE_URL}/${id}`
            );

          if (
            response.status >= 200 &&
            response.status < 300
          ) {
            return id;
          }

          return rejectWithValue(
            `Unable to delete post: HTTP ${response.status}`
          );
        } catch (error) {
          return rejectWithValue(
            error.response?.data?.error ||
            error.message ||
            'Unable to delete post.'
          );
        }
      }
    ),
};

const postsSlice = createSlice({
  name: FeatureName,

  initialState:
    postsAdapter.getInitialState({
      status: 'idle',
      error: null,
    }),

  reducers: {
    reactionAdded(state, action) {
      const {
        postId,
        reaction,
      } = action.payload;

      const existingPost =
        state.entities[postId];

      if (
        existingPost &&
        existingPost.reactions &&
        Object.prototype.hasOwnProperty.call(
          existingPost.reactions,
          reaction
        )
      ) {
        existingPost.reactions[
          reaction
        ] += 1;
      }
    },
  },

  extraReducers(builder) {
    builder
      .addCase(
        fetchers[
          `fetch${FeatureForCamelCase}`
        ].pending,
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )

      .addCase(
        fetchers[
          `fetch${FeatureForCamelCase}`
        ].fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.error = null;

          postsAdapter.setAll(
            state,
            action.payload
          );
        }
      )

      .addCase(
        fetchers[
          `fetch${FeatureForCamelCase}`
        ].rejected,
        (state, action) => {
          state.status = 'failed';

          state.error =
            action.payload ||
            action.error.message ||
            'Unable to load posts.';
        }
      )

      .addCase(
        adders[
          `addNew${FeatureForCamelCaseCRUD}`
        ].fulfilled,
        (state, action) => {
          postsAdapter.addOne(
            state,
            action.payload
          );

          state.error = null;
        }
      )

      .addCase(
        adders[
          `addNew${FeatureForCamelCaseCRUD}`
        ].rejected,
        (state, action) => {
          state.error =
            action.payload ||
            action.error.message;
        }
      )

      .addCase(
        updaters[
          `update${FeatureForCamelCaseCRUD}`
        ].fulfilled,
        (state, action) => {
          postsAdapter.upsertOne(
            state,
            action.payload
          );

          state.error = null;
        }
      )

      .addCase(
        updaters[
          `update${FeatureForCamelCaseCRUD}`
        ].rejected,
        (state, action) => {
          state.error =
            action.payload ||
            action.error.message;
        }
      )

      .addCase(
        deleters[
          `delete${FeatureForCamelCaseCRUD}`
        ].fulfilled,
        (state, action) => {
          postsAdapter.removeOne(
            state,
            action.payload
          );

          state.error = null;
        }
      )

      .addCase(
        deleters[
          `delete${FeatureForCamelCaseCRUD}`
        ].rejected,
        (state, action) => {
          state.error =
            action.payload ||
            action.error.message;
        }
      );
  },
});

export const {
  selectAll: selectAllItems,
  selectById: selectItemById,
  selectIds: selectItemIds,
} = postsAdapter.getSelectors(
  (state) => state[FeatureName]
);

export const getItemsStatus =
  (state) =>
    state[FeatureName].status;

export const getItemsError =
  (state) =>
    state[FeatureName].error;

export const selectPostsByUser =
  createSelector(
    [
      selectAllItems,
      (state, userId) => userId,
    ],
    (posts, userId) =>
      posts.filter(
        (post) =>
          post.userId === userId
      )
  );

export const {
  reactionAdded,
} = postsSlice.actions;

export default postsSlice.reducer;
